/**
 * Subtasks Redux Slice
 */

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { Subtask, CreateSubtaskDto, UpdateSubtaskDto } from '../../api/types/SubtaskTypes';
import { subtasksApi } from '../../api/endpoints/subtasksApi';

// ==================== State Interface ====================

interface SubtasksState {
  // Subtasks by task ID
  subtasksByTask: { [taskId: string]: Subtask[] };
  loading: boolean;
  error: string | null;
}

const initialState: SubtasksState = {
  subtasksByTask: {},
  loading: false,
  error: null,
};

// ==================== Async Thunks ====================

/**
 * Fetch subtasks for a task
 */
export const fetchTaskSubtasks = createAsyncThunk(
  'subtasks/fetchTaskSubtasks',
  async (taskId: string) => {
    const subtasks = await subtasksApi.getTaskSubtasks(taskId);
    return { taskId, subtasks };
  },
);

/**
 * Create a subtask
 */
export const createSubtask = createAsyncThunk(
  'subtasks/createSubtask',
  async ({ taskId, data }: { taskId: string; data: CreateSubtaskDto }) => {
    const subtask = await subtasksApi.createSubtask(taskId, data);
    return { taskId, subtask };
  },
);

/**
 * Update a subtask
 */
export const updateSubtask = createAsyncThunk(
  'subtasks/updateSubtask',
  async ({
    taskId,
    subtaskId,
    data,
  }: {
    taskId: string;
    subtaskId: string;
    data: UpdateSubtaskDto;
  }) => {
    const subtask = await subtasksApi.updateSubtask(taskId, subtaskId, data);
    return { taskId, subtask };
  },
);

/**
 * Delete a subtask
 */
export const deleteSubtask = createAsyncThunk(
  'subtasks/deleteSubtask',
  async ({ taskId, subtaskId }: { taskId: string; subtaskId: string }) => {
    await subtasksApi.deleteSubtask(taskId, subtaskId);
    return { taskId, subtaskId };
  },
);

// ==================== Slice ====================

const subtasksSlice = createSlice({
  name: 'subtasks',
  initialState,
  reducers: {
    clearTaskSubtasks(state, action) {
      const taskId = action.payload;
      delete state.subtasksByTask[taskId];
    },
    clearErrors(state) {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    // ==================== Fetch Task Subtasks ====================
    builder
      .addCase(fetchTaskSubtasks.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTaskSubtasks.fulfilled, (state, action) => {
        state.loading = false;
        const { taskId, subtasks } = action.payload;
        state.subtasksByTask[taskId] = subtasks;
      })
      .addCase(fetchTaskSubtasks.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch subtasks';
      });

    // ==================== Create Subtask ====================
    builder
      .addCase(createSubtask.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createSubtask.fulfilled, (state, action) => {
        state.loading = false;
        const { taskId, subtask } = action.payload;
        if (!state.subtasksByTask[taskId]) {
          state.subtasksByTask[taskId] = [];
        }
        state.subtasksByTask[taskId].push(subtask);
      })
      .addCase(createSubtask.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to create subtask';
      });

    // ==================== Update Subtask ====================
    builder
      .addCase(updateSubtask.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateSubtask.fulfilled, (state, action) => {
        state.loading = false;
        const { taskId, subtask } = action.payload;
        if (state.subtasksByTask[taskId]) {
          const index = state.subtasksByTask[taskId].findIndex((s) => s.id === subtask.id);
          if (index !== -1) {
            state.subtasksByTask[taskId][index] = subtask;
          }
        }
      })
      .addCase(updateSubtask.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to update subtask';
      });

    // ==================== Delete Subtask ====================
    builder
      .addCase(deleteSubtask.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteSubtask.fulfilled, (state, action) => {
        state.loading = false;
        const { taskId, subtaskId } = action.payload;
        if (state.subtasksByTask[taskId]) {
          state.subtasksByTask[taskId] = state.subtasksByTask[taskId].filter(
            (s) => s.id !== subtaskId,
          );
        }
      })
      .addCase(deleteSubtask.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to delete subtask';
      });
  },
});

// ==================== Exports ====================

export const { clearTaskSubtasks, clearErrors } = subtasksSlice.actions;

export default subtasksSlice.reducer;
